/**
 * Componente PIPWindow
 * 
 * Componente principal da janela Picture-in-Picture
 * Renderiza o conteúdo completo: timer, controles, comentários e seletor de TASKS
 * 
 * @author VISION DESK
 * @version 0.24.0
 */

import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, ExternalLink, Activity } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PIPControls } from './PIPControls';
import { PIPComments } from './PIPComments';
import { PIPOSSelector } from './PIPOSSelector';
import { osApi } from '@/services/api';
import { listActiveSessions, formatDuration, calculateRealTimeDuration } from '@/services/workSessionApi';
import { osCommentApi } from '@/services/osCommentApi';
import type { PIPWindowProps } from '@/types/pictureInPicture';

export const PIPWindow: React.FC<PIPWindowProps> = ({
  osId: initialOSId,
  sessionId: initialSessionId,
  pipWindow,
  onClose,
  isFallbackMode = false,
}) => {
  const queryClient = useQueryClient();
  const [currentOSId, setCurrentOSId] = useState(initialOSId);
  const [currentSessionId, setCurrentSessionId] = useState(initialSessionId);

  // Buscar dados do Job/Task (CORRIGIDO: endpoint correto)
  const { data: os, refetch: refetchOS } = useQuery({
    queryKey: ['os-pip', currentOSId],
    queryFn: async () => {
      const response = await osApi.getById(currentOSId);
      // API retorna { success: true, data: {...} }
      return response.data?.data || response.data;
    },
    refetchInterval: 30000, // Atualizar a cada 30 segundos
    enabled: !!currentOSId,
  });

  // Buscar sessão ativa
  const { data: session, refetch: refetchSession, isLoading: isLoadingSession } = useQuery({
    queryKey: ['session-pip', currentSessionId],
    queryFn: async () => {
      const activeSessions = await listActiveSessions();
      const foundSession = activeSessions.sessions.find((s: any) => s.id === currentSessionId);
      
      // Log de debug para diagnosticar problemas de estado
      if (foundSession) {
        // console.log('🔍 PIP: Sessão atualizada:', {
        // id: foundSession.id.slice(0, 8),
        // isActive: foundSession.isActive,
        // isPaused: foundSession.isPaused,
        // osId: foundSession.orderOfServiceId.slice(0, 8)
        // });
      } else {
        // console.warn('⚠️ PIP: Sessão não encontrada na lista de ativas:', currentSessionId.slice(0, 8));
        // console.warn('🔍 PIP: Total de sessões retornadas pela API:', activeSessions.sessions.length);
        // console.warn('❌ PIP: Sessão foi finalizada ou removida - auto-switch será acionado');
      }
      
      return foundSession;
    },
    refetchInterval: 1000, // Atualizar timer a cada 1 segundo
    enabled: !!currentSessionId,
    staleTime: 0, // Sempre considerar dados como stale
    cacheTime: 0, // Não manter cache
    keepPreviousData: false, // NÃO manter dados antigos quando query muda
    retry: 3, // Tentar 3 vezes antes de considerar falha
    retryDelay: 1000, // Aguardar 1 segundo entre tentativas
  });

  // Buscar comentários recentes (INTEGRADO: API real)
  const { data: commentsData, refetch: refetchComments } = useQuery({
    queryKey: ['os-comments-pip', currentOSId],
    queryFn: async () => {
      const response = await osCommentApi.listComments(currentOSId, 1, 3);
      return {
        comments: response.data || [],
        total: response.pagination?.total || 0,
      };
    },
    refetchInterval: 10000, // Atualizar a cada 10 segundos
    enabled: !!currentOSId,
  });

  // Callback para trocar de TASK (memoizado para evitar re-renders desnecessários)
  const handleSelectOS = useCallback((newOSId: string, newSessionId: string) => {
    // console.log('📺 PIP: Trocando para TASK:', newOSId);
    setCurrentOSId(newOSId);
    setCurrentSessionId(newSessionId);
  }, []);

  // Callback para refresh dos dados
  // CORREÇÃO v0.28.9: Invalidar cache ANTES de refetch para garantir dados frescos
  const handleRefresh = async () => {
    // console.log('🔄 PIP handleRefresh: Invalidando cache e recarregando dados...');
    
    // Invalidar todas as queries relevantes
    await queryClient.invalidateQueries({ queryKey: ['session-pip', currentSessionId] });
    await queryClient.invalidateQueries({ queryKey: ['os-pip', currentOSId] });
    await queryClient.invalidateQueries({ queryKey: ['os-comments-pip', currentOSId] });
    
    // Refetch manual para garantir dados atualizados
    await Promise.all([
      refetchOS(),
      refetchSession(),
      refetchComments()
    ]);
    
    // console.log('✅ PIP handleRefresh: Dados recarregados');
  };

  /**
   * Efeito para trocar automaticamente para próxima sessão ativa quando atual for parada
   * Monitora mudanças na sessão atual e busca próxima sessão disponível
   * 
   * CORREÇÃO v0.28.9:
   * - Aguarda query carregar antes de verificar se sessão não existe
   * - Adiciona delay antes de buscar outra sessão (evita race condition)
   * - Detecta quando sessão foi finalizada (isActive: false) OU não existe mais
   * - TENTA TROCAR para próxima sessão (prioriza em execução, aceita pausada)
   * - SÓ FECHA se não houver NENHUMA outra sessão disponível
   * - Prioridade: Em Execução > Pausada > Fechar PIP
   */
  useEffect(() => {
    // console.log('🔄 PIP useEffect: Verificando estado da sessão...', {
    // hasSession: !!session,
    // isLoadingSession,
    // currentSessionId: currentSessionId?.slice(0, 8),
    // sessionIsActive: session?.isActive,
    // sessionIsPaused: session?.isPaused
    // });
    
    // CORREÇÃO CRÍTICA: Aguardar query carregar antes de verificar se sessão não existe
    // Isso evita race condition onde a query ainda está carregando
    if (isLoadingSession) {
      // console.log('⏳ PIP: Aguardando carregamento da sessão...');
      return;
    }
    
    // CORREÇÃO CRÍTICA 1: Se sessão não existe mais na lista, buscar outra sessão
    // Adicionar delay para evitar fechar imediatamente após abrir (race condition)
    if (!session && currentSessionId) {
      // console.warn('⚠️ PIP: Sessão não encontrada, aguardando 3 segundos antes de buscar outra...');
      // console.warn('📊 PIP: SessionId:', currentSessionId.slice(0, 8));
      
      // Aguardar 3 segundos antes de buscar outra sessão (evita race condition)
      // Isso dá tempo para a query carregar e para a sessão aparecer na lista
      const timeoutId = setTimeout(() => {
        // Primeiro, tentar refetch da sessão atual (pode ter sido carregada entretanto)
        refetchSession().then(() => {
          // Aguardar um pouco mais para a query atualizar
          setTimeout(() => {
            // Buscar todas as sessões ativas do usuário
            listActiveSessions().then((data) => {
              const activeSessions = data?.sessions || [];
              
              // Verificar se a sessão atual está na lista (pode ter sido carregada entretanto)
              const currentSessionFound = activeSessions.find((s: any) => s.id === currentSessionId);
              
              if (currentSessionFound) {
                // console.log('✅ PIP: Sessão encontrada após delay, continuando...');
                return; // Sessão existe, não fazer nada
              }
              
              // console.error('❌ PIP: Sessão não existe mais na lista de ativas, buscando outra sessão...');
              
              // Buscar todas as sessões disponíveis (incluindo a atual se existir)
              if (activeSessions.length > 0) {
                // PRIORIDADE 1: Buscar sessão EM EXECUÇÃO (não pausada)
                const runningSession = activeSessions.find((s: any) => !s.isPaused);
                
                if (runningSession) {
                  // console.log('✅ PIP: Encontrada sessão EM EXECUÇÃO, trocando...');
                  handleSelectOS(runningSession.orderOfServiceId, runningSession.id);
                  return;
                } else {
                  // PRIORIDADE 2: Se não houver em execução, usar PAUSADA
                  const pausedSession = activeSessions[0];
                  // console.log('⚠️ PIP: Apenas sessões pausadas disponíveis, trocando para pausada...');
                  handleSelectOS(pausedSession.orderOfServiceId, pausedSession.id);
                  return;
                }
              } else {
                // Nenhuma sessão disponível, fechar PIP
                // console.error('🚨 PIP: Nenhuma sessão disponível, fechando PIP...');
                onClose();
              }
            }).catch((error) => {
              // console.error('❌ PIP: Erro ao buscar sessões ativas:', error);
              // Não fechar imediatamente em caso de erro, tentar novamente depois
              // console.log('⏳ PIP: Aguardando 3 segundos antes de tentar novamente...');
              setTimeout(() => {
                refetchSession();
              }, 3000);
            });
          }, 500); // Aguardar 500ms após refetch
        });
      }, 3000); // Aguardar 3 segundos antes de buscar outra sessão
      
      return () => clearTimeout(timeoutId);
    }
    
    // CORREÇÃO CRÍTICA 2: Detectar quando sessão foi finalizada (isActive: false)
    if (session && !session.isActive) {
      // console.log('⚠️ PIP: Sessão atual foi finalizada, buscando próxima sessão...');
      // console.log('📊 PIP: Estado da sessão:', {
      // id: session.id.slice(0, 8),
      // isActive: session.isActive,
      // isPaused: session.isPaused,
      // osId: currentOSId.slice(0, 8)
      // });
      
      // Buscar todas as sessões ativas do usuário
      listActiveSessions().then((data) => {
        const activeSessions = data?.sessions || [];
        // console.log(`📋 PIP: Total de sessões retornadas: ${activeSessions.length}`);
        
        // ESTRATÉGIA: Priorizar sessões em execução, mas aceitar pausadas
        // Filtrar sessões diferentes da atual
        const otherSessions = activeSessions.filter(
          (s: any) => s.orderOfServiceId !== currentOSId
        );
        
        // console.log(`📋 PIP: Outras sessões disponíveis: ${otherSessions.length}`);
        
        if (otherSessions.length > 0) {
          // PRIORIDADE 1: Buscar sessão EM EXECUÇÃO (não pausada)
          const runningSession = otherSessions.find((s: any) => !s.isPaused);
          
          if (runningSession) {
            // console.log('✅ PIP: Encontrada sessão EM EXECUÇÃO, trocando...');
            // console.log('🔄 PIP: Sessão destino:', {
            // id: runningSession.id.slice(0, 8),
            // osId: runningSession.orderOfServiceId.slice(0, 8),
            // osTitle: runningSession.orderOfService?.title,
            // isPaused: false
            // });
            handleSelectOS(runningSession.orderOfServiceId, runningSession.id);
          } else {
            // PRIORIDADE 2: Se não houver em execução, usar PAUSADA
            const pausedSession = otherSessions[0];
            // console.log('⚠️ PIP: Apenas sessões pausadas disponíveis, trocando para pausada...');
            // console.log('🔄 PIP: Sessão destino (pausada):', {
            // id: pausedSession.id.slice(0, 8),
            // osId: pausedSession.orderOfServiceId.slice(0, 8),
            // osTitle: pausedSession.orderOfService?.title,
            // isPaused: pausedSession.isPaused
            // });
            handleSelectOS(pausedSession.orderOfServiceId, pausedSession.id);
          }
        } else {
          // Nenhuma outra sessão disponível, fechar PIP
          // console.log('🚨 PIP: Nenhuma outra sessão disponível, fechando PIP...');
          onClose();
        }
      }).catch((error) => {
        // console.error('❌ PIP: Erro ao buscar sessões ativas:', error);
        onClose();
      });
    }
  }, [session, currentSessionId, currentOSId, onClose, handleSelectOS, isLoadingSession, refetchSession]);  // handleSelectOS memoizado, não causa re-renders

  // Abrir TASK no navegador principal
  const handleOpenInBrowser = () => {
    if (window.opener && !window.opener.closed) {
      window.opener.focus();
      window.opener.location.href = `/os/${currentOSId}`;
    } else {
      // Se janela principal não existe, abrir em nova aba
      window.open(`/os/${currentOSId}`, '_blank');
    }
  };

  // Calcular duração em tempo real (CORRIGIDO: mesma lógica do ActiveSessionsWidget)
  const [currentDuration, setCurrentDuration] = useState(0);
  
  useEffect(() => {
    if (!session) {
      setCurrentDuration(0);
      return;
    }

    // Função para atualizar duração
    const updateDuration = () => {
      const duration = calculateRealTimeDuration(session);
      setCurrentDuration(duration);
    };

    // Atualizar imediatamente
    updateDuration();

    // Se sessão está ativa (em execução ou pausada), atualizar a cada 1 segundo
    if (session.isActive) {
      const interval = setInterval(updateDuration, 1000);
      return () => clearInterval(interval);
    }
  }, [session]);

  /**
   * Fechar PIP automaticamente ao retornar ao navegador principal
   * Usa Page Visibility API para detectar quando usuário volta
   */
  useEffect(() => {
    const handleVisibilityChange = () => {
      // Se página voltou a ficar visível (usuário retornou ao navegador), fechar PIP
      if (!document.hidden) {
        // console.log('👁️ PIP: Usuário retornou ao navegador principal, fechando PIP automaticamente...');
        onClose();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [onClose]);

  // Conteúdo do PIP
  const content = (
    <div
      className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 text-white p-4 flex flex-col h-[806px] overflow-hidden"
      style={{ height: (pipWindow?.innerHeight ?? 806) }}
    >
      {/* DIAGNÓSTICO DE LAYOUT */}
      {process.env.NODE_ENV !== 'production' && (
        <div className="hidden" aria-hidden>
          {console.log('📺 PIP DIAG | render', {
            hasSession: !!session,
            isPaused: session?.isPaused,
            isActive: session?.isActive,
            layout: 'header + timer + [scroll(comments)] + selector + footer',
          })}
        </div>
      )}

      {/* Header - Altura fixa */}
      <div className="flex-none flex items-start justify-between mb-3 pb-2 border-b border-white/10">
        <div className="flex-1 min-w-0 pr-2">
          <h2 className="text-base font-bold truncate">
            {os?.title || 'Carregando...'}
          </h2>
          <p className="text-xs text-gray-400 mt-1">
            TASK #{currentOSId.slice(0, 8)}
          </p>
        </div>
        <button
          onClick={onClose}
          className="flex-none p-1.5 hover:bg-white/10 rounded transition-colors"
          title="Fechar janela PIP"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Timer Display - Altura fixa */}
      <div className="flex-none bg-white/5 backdrop-blur-sm rounded-lg p-3 mb-3">
        <div className="text-center mb-2">
          <div className="text-3xl font-bold font-mono tracking-wider">
            {formatDuration(currentDuration)}
          </div>
          <div className="flex items-center justify-center gap-2 mt-2">
            <Activity
              className={`w-4 h-4 ${
                session?.isActive && !session?.isPaused
                  ? 'text-green-400 animate-pulse'
                  : 'text-gray-400'
              }`}
            />
            <span className="text-xs text-gray-400">
              {!session?.isActive
                ? 'Sessão inativa'
                : session?.isPaused
                ? 'Pausada'
                : 'Em execução'}
            </span>
          </div>
        </div>

        {/* Controles - CORREÇÃO v0.28.9: Passa documento PIP para renderizar modais */}
        <PIPControls
          osId={currentOSId}
          sessionId={currentSessionId}
          session={session}
          onRefresh={handleRefresh}
          targetDocument={pipWindow?.document}
        />
      </div>

      {/* Área com Scroll - SOMENTE Comentários (seletor fora do scroll para manter footer visível) */}
      <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar mb-3">
        <div className="bg-white/5 backdrop-blur-sm rounded-lg p-3 mb-3">
          <PIPComments
            osId={currentOSId}
            comments={commentsData?.comments || []}
            onCommentAdded={handleRefresh}
          />
        </div>
      </div>

      {/* Seletor de TASKS - Fora da área de scroll */}
      <div className="flex-none mb-3">
        {process.env.NODE_ENV !== 'production' && console.log('📺 PIP DIAG | selector:outside-scroll')}
        <PIPOSSelector
          currentOSId={currentOSId}
          onSelectOS={handleSelectOS}
        />
      </div>

      {/* Footer - Link para Navegador - SEMPRE VISÍVEL - Altura fixa */}
      <div className="flex-none pt-3 border-t border-white/10">
        {process.env.NODE_ENV !== 'production' && console.log('📺 PIP DIAG | footer:visible')}
        <button
          onClick={handleOpenInBrowser}
          className="w-full py-2.5 px-4 bg-blue-500/20 hover:bg-blue-500/30
                   rounded-lg transition-colors text-sm font-medium
                   flex items-center justify-center gap-2"
        >
          <ExternalLink className="w-4 h-4" />
          Abrir no Navegador Principal
        </button>
      </div>

      {/* Custom scrollbar styles - Scrollbar roxa visível para TODAS as áreas */}
      <style>{`
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: rgba(147, 51, 234, 0.5) rgba(255, 255, 255, 0.05);
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 4px;
          margin: 4px 0;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(147, 51, 234, 0.5);
          border-radius: 4px;
          border: 1px solid rgba(147, 51, 234, 0.2);
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(147, 51, 234, 0.7);
          border-color: rgba(147, 51, 234, 0.4);
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:active {
          background: rgba(147, 51, 234, 0.9);
        }
      `}</style>
    </div>
  );

  // Modo Fallback (Modal)
  if (isFallbackMode) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center">
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/80 backdrop-blur-sm"
          onClick={onClose}
        />
        
        {/* Modal PIP */}
        <div
          className="relative w-[326px] h-[806px] rounded-lg overflow-hidden
                     shadow-2xl border border-white/20"
          onClick={(e) => e.stopPropagation()}
        >
          {content}
        </div>
      </div>
    );
  }

  // Modo Nativo (PIP)
  if (!pipWindow) return null;

  const container = (pipWindow as any).document.getElementById('pip-root');
  return container ? createPortal(content, container) : null;
};
